"use client";
import { SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  InputRef,
  Space,
  Table,
  TableColumnType,
  TableProps,
  Typography,
} from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import { useRef } from "react";
import { get, orderBy } from "lodash";
import { CapabilityTags, FleetName, VehicleInfo } from "@/Components";
import UserInfo from "@/Components/UserDetailsInfo";
import ShortIdParagraph from "../ShortIdParagraph";
import { useRetrieveTitle } from "@/Utils/helpers";

const { Paragraph } = Typography;

interface DriverTableProps {
  liveLocationData: any[];
  activeFilters: string[];
  activeStatus: string[];
  isLoading: boolean;
}

type AlignType = "left" | "right" | "center";

const getAlign = (isRTL: boolean, center = false): AlignType => {
  if (center) return "center";
  return isRTL ? "right" : "left";
};

const TableView: React.FC<DriverTableProps> = ({
  liveLocationData,
  activeFilters,
  activeStatus,
  isLoading,
}) => {
  const { getTitle, isRTL } = useRetrieveTitle();
  const searchInput = useRef<InputRef>(null);

  const getColumnSearchProps = (dataIndex: any): TableColumnType<any> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`${getTitle("search")} ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
          >
            {getTitle("search")}
          </Button>
          <Button onClick={() => clearFilters?.()} size="small">
            {getTitle("reset")}
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            {getTitle("close")}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  });

  const columns: TableProps<Record<string, any>>["columns"] = [
    {
      title: getTitle("driver_id"),
      dataIndex: "driver_id",
      key: "driver_id",
      align: getAlign(isRTL),
      sorter: (a, b) => {
        const driverIdA = a.driver_id;
        const driverIdB = b.driver_id;
        return driverIdA.localeCompare(driverIdB);
      },
      render: (driver_id: string) => (
        <UserInfo
          driverId={driver_id}
          showDriverId={true}
          showShortDriverID={true}
        />
      ),
      ...getColumnSearchProps("driver_id"),
    },
    {
      title: getTitle("fleet_id"),
      dataIndex: "fleet_id",
      key: "fleet_id",
      align: getAlign(isRTL),
      render: (fleet_id) => (
        <div>
          <FleetName
            id={fleet_id}
            style={{ color: "rgba(0, 29, 82, 0.88)", fontWeight: "bold" }}
          />
          <ShortIdParagraph idValue={fleet_id} />
        </div>
      ),
      sorter: (a, b) => {
        const driverIdA = a.fleet_id;
        const driverIdB = b.fleet_id;
        return driverIdA.localeCompare(driverIdB);
      },
      ...getColumnSearchProps("fleet_id"),
    },
    {
      title: getTitle("vehicle_info"),
      dataIndex: "vehicle_id",
      key: "vehicle_id",
      align: getAlign(isRTL),
      render: (vehicle_id) =>
        vehicle_id ? (
          <VehicleInfo id={vehicle_id} />
        ) : (
          <i>{getTitle("vehicle_not_found")}</i>
        ),
      sorter: (a, b) => {
        const driverIdA = a.vehicle_id;
        const driverIdB = b.vehicle_id;
        return driverIdA.localeCompare(driverIdB);
      },
      ...getColumnSearchProps("vehicle_id"),
    },
    {
      title: getTitle("driver_status"),
      dataIndex: "driver_status",
      key: "driver_status",
      align: "center",
      render: (driver_status) => (
        <h3 style={{ textTransform: "capitalize" }}>
          <Paragraph>{driver_status}</Paragraph>
        </h3>
      ),
      ...getColumnSearchProps("driver_status"),
    },
    {
      title: getTitle("dispatch_count"),
      dataIndex: "dispatch_count",
      key: "dispatch_count",
      align: "center",
      render: (dispatch_count) => (
        <h3>
          <Paragraph>{dispatch_count}</Paragraph>
        </h3>
      ),
      sorter: (a, b) => a.dispatch_count - b.dispatch_count,
      ...getColumnSearchProps("dispatch_count"),
    },
    {
      title: getTitle("latitude"),
      dataIndex: "latitude",
      key: "latitude",
      align: "center",
      render: (latitude) => (
        <h3>
          <Paragraph>{latitude}</Paragraph>
        </h3>
      ),
    },
    {
      title: getTitle("longitude"),
      dataIndex: "longitude",
      key: "longitude",
      align: "center",
      render: (longitude) => (
        <h3>
          <Paragraph>{longitude}</Paragraph>
        </h3>
      ),
    },
    {
      title: getTitle("device_id"),
      dataIndex: "device_id",
      key: "device_id",
      align: "center",
      render: (device_id) =>
        device_id ? (
          <h3>
            <Paragraph>{device_id}</Paragraph>
          </h3>
        ) : (
          "--"
        ),
      ...getColumnSearchProps("device_id"),
    },
    {
      title: getTitle("capabilities"),
      dataIndex: "capabilities",
      key: "capabilities",
      align: "center",
      width: 350,
      render: (capabilities) =>
        capabilities.length ? (
          <CapabilityTags capabilityArr={capabilities} />
        ) : (
          <i>{getTitle("no_capabilites_found")}.</i>
        ),
    },
    {
      title: getTitle("ref_id"),
      dataIndex: "ref_id",
      key: "ref_id",
      align: "center",
      render: (ref_id: string) =>
        ref_id ? (
          <ShortIdParagraph idValue={ref_id} />
        ) : (
          "--"
        ),
      ...getColumnSearchProps("ref_id"),
    },
    {
      title: getTitle("ref_status"),
      dataIndex: "ref_status",
      key: "ref_status",
      align: "center",
      render: (ref_status: string) =>
        ref_status ? (
          <Paragraph>{ref_status}</Paragraph>
        ) : (
          "--"
        ),
      ...getColumnSearchProps("ref_status"),
    },

    {
      title: getTitle("destination_lat"),
      dataIndex: "dest_lat",
      key: "dest_lat",
      align: "center",
      render: (dest_lat) => (
        <h3>
          <Paragraph>{dest_lat}</Paragraph>
        </h3>
      ),
      sorter: (a, b) => a.dest_lat - b.dest_lat,
    },
    {
      title: getTitle("destination_lng"),
      dataIndex: "dest_lng",
      key: "dest_lng",
      align: "center",
      render: (dest_lng) => (
        <h3>
          <Paragraph>{dest_lng}</Paragraph>
        </h3>
      ),
      sorter: (a, b) => a.dest_lng - b.dest_lng,
    },

  ];

  const getFilteredAndSortedData = () => {
    let filtered = liveLocationData;

    if (activeFilters.length) {
      filtered = filtered.filter((location: any) =>
        location?.properties?.capabilities?.some((id: string) =>
          activeFilters.includes(id)
        )
      );
    }

    if (activeStatus.length) {
      filtered = filtered.filter((location: any) =>
        activeStatus.includes(location?.properties?.driver_status)
      );
    }

    return orderBy(filtered, ["properties.driver_id"], ["desc"]);
  };

  const mapSourceData = getFilteredAndSortedData();
  const tableScoreData = mapSourceData.map((loc: any) => loc.properties);

  return (
    <div className="tableCardWrapper">
      <Card title={<h3 style={{ textTransform: 'capitalize' }}>{getTitle("live_location_data")}</h3>}>
        <Table
          loading={isLoading}
          rowKey="driver_id"
          dataSource={tableScoreData}
          columns={columns}
          pagination={{ pageSize: 6 }}
          scroll={{ x: "max-content" }}
        />
      </Card>
    </div>
  );
};

export default TableView;
